import { z } from 'zod'
import { pageQuery, uuid } from './common.ts'

export const BILL_STATUSES = ['draft', 'final', 'cancelled'] as const
export type BillStatus = (typeof BILL_STATUSES)[number]

export const createBill = z.object({
  id: uuid,
  prescriptionId: uuid,
})

export const cancelBill = z.object({
  reason: z.string().trim().min(3, 'Say why').max(500),
  version: z.number().int().positive(),
})

export const finaliseBill = z.object({
  version: z.number().int().positive(),
  // A fingerprint of the figures the operator was actually looking at.
  // Without it a doctor could change the prescription in the seconds
  // between the screen rendering and the button being pressed, and the
  // bill would be issued for an amount nobody ever saw.
  fingerprint: z.string().trim().length(16).optional(),
})

export const billFilters = pageQuery.extend({
  status: z.enum(BILL_STATUSES).optional(),
})

/**
 * Which statuses a role may see.
 *
 * Cancelled bills are the proprietor's business. For everyone else they
 * are not hidden in the interface and fetched anyway -- they are excluded
 * from the query, so a crafted request returns nothing either.
 */
export const visibleStatuses = (canVoid: boolean): readonly BillStatus[] =>
  canVoid ? BILL_STATUSES : BILL_STATUSES.filter(s => s !== 'cancelled')

export const PAYMENT_METHODS = ['cash', 'upi', 'card', 'other'] as const
export type PaymentMethod = (typeof PAYMENT_METHODS)[number]

export const recordPayment = z.object({
  method: z.enum(PAYMENT_METHODS),
  // What was actually taken. Usually the total, but it is recorded rather
  // than assumed so a short payment is visible instead of invisible.
  amount: z.union([z.string(), z.number()])
    .transform(v => String(v).trim())
    .refine(v => /^\d{1,9}(\.\d{1,2})?$/.test(v), { message: 'Enter an amount like 356.12' }),
  version: z.number().int().positive(),
})
